<script setup>
import { computed, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  setupMode: { type: Boolean, default: false },
  industryHints: { type: Object, default: () => ({}) },
  suggestedQuestions: { type: Array, default: () => [] },
});

const model = defineModel({ type: Object, default: () => ({}) });

const UI_LABELS = {
  title: 'Bokningsunderlag',
  enabled: 'Ta emot bokningsförfrågningar',
  availableDays: 'Tillgängliga dagar',
  availableTimes: 'Tillgängliga tider',
  instructions: 'Bokningsregler',
  testTitle: 'Lägg till bokningsförfrågan',
  customerName: 'Kundens namn',
  customerContact: 'Telefon eller e-post',
  service: 'Tjänst',
  requestedTime: 'Önskad tid',
  note: 'Anteckning',
  add: 'Lägg till',
  requests: 'Bokningsförfrågningar',
  empty: 'Inga bokningsförfrågningar ännu.',
  suggestedQuestions: 'Bokningsfrågor för vald verksamhet',
  example: 'Exempel:',
};

const STATUS_OPTIONS = [
  { value: 'requested', label: 'Förfrågad' },
  { value: 'confirmed', label: 'Bekräftad' },
  { value: 'cancelled', label: 'Avbokad' },
];

const TEXT_SEPARATOR = '-';
const DOT_SEPARATOR = '·';

const draftRequest = ref({
  customer_name: '',
  customer_contact: '',
  service: '',
  requested_time: '',
  note: '',
});

const booking = computed({
  get: () => ({
    enabled: false,
    available_days: '',
    available_times: '',
    instructions: '',
    requests: [],
    ...model.value,
  }),
  set: value => {
    model.value = value;
  },
});

const updateField = (field, value) => {
  booking.value = { ...booking.value, [field]: value };
};

const updateRequestStatus = (requestId, status) => {
  booking.value = {
    ...booking.value,
    requests: booking.value.requests.map(request =>
      request.id === requestId ? { ...request, status } : request
    ),
  };
};

const canAddRequest = computed(
  () =>
    draftRequest.value.customer_name.trim() &&
    draftRequest.value.customer_contact.trim() &&
    draftRequest.value.requested_time.trim()
);

const addRequest = () => {
  if (!canAddRequest.value) return;

  booking.value = {
    ...booking.value,
    requests: [
      ...booking.value.requests,
      {
        id: crypto.randomUUID(),
        customer_name: draftRequest.value.customer_name.trim(),
        customer_contact: draftRequest.value.customer_contact.trim(),
        service: draftRequest.value.service.trim(),
        requested_time: draftRequest.value.requested_time.trim(),
        note: draftRequest.value.note.trim(),
        status: 'requested',
        created_at: new Date().toISOString(),
      },
    ],
  };

  draftRequest.value = {
    customer_name: '',
    customer_contact: '',
    service: '',
    requested_time: '',
    note: '',
  };
};
</script>

<template>
  <section class="grid gap-4 rounded-md border border-n-weak p-4">
    <div class="flex items-center justify-between gap-3">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.title }}
      </h3>
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input
          type="checkbox"
          class="size-4"
          :checked="booking.enabled"
          @change="updateField('enabled', $event.target.checked)"
        />
        <span>{{ UI_LABELS.enabled }}</span>
      </label>
    </div>

    <p
      v-if="setupMode && industryHints.booking_description_sv"
      class="mb-0 text-sm text-n-slate-10"
    >
      {{ industryHints.booking_description_sv }}
    </p>

    <div
      v-if="setupMode && suggestedQuestions.length"
      class="grid gap-2 rounded-md bg-n-alpha-2 p-3"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.suggestedQuestions }}
      </span>
      <div
        v-for="question in suggestedQuestions"
        :key="question.id"
        class="text-sm text-n-slate-11"
      >
        <span class="font-medium text-n-slate-12">
          {{ question.label_sv }}
        </span>
        <span v-if="question.example_sv" class="text-n-slate-10">
          {{ ` ${UI_LABELS.example} ${question.example_sv}` }}
        </span>
      </div>
    </div>

    <div v-if="booking.enabled" class="grid gap-4 sm:grid-cols-2">
      <Input
        :model-value="booking.available_days"
        :label="UI_LABELS.availableDays"
        :placeholder="props.industryHints.booking_days_placeholder_sv || ''"
        size="sm"
        @update:model-value="updateField('available_days', $event)"
      />
      <Input
        :model-value="booking.available_times"
        :label="UI_LABELS.availableTimes"
        :placeholder="props.industryHints.booking_times_placeholder_sv || ''"
        size="sm"
        @update:model-value="updateField('available_times', $event)"
      />
    </div>
    <Textarea
      v-if="booking.enabled"
      :model-value="booking.instructions"
      :label="UI_LABELS.instructions"
      :placeholder="props.industryHints.booking_rules_placeholder_sv || ''"
      :max-length="1000"
      resize
      @update:model-value="updateField('instructions', $event)"
    />

    <div
      v-if="booking.enabled && !setupMode"
      class="grid gap-3 rounded-md bg-n-alpha-2 p-3"
    >
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.testTitle }}
      </h4>
      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="draftRequest.customer_name"
          :label="UI_LABELS.customerName"
          size="sm"
        />
        <Input
          v-model="draftRequest.customer_contact"
          :label="UI_LABELS.customerContact"
          size="sm"
        />
        <Input
          v-model="draftRequest.service"
          :label="UI_LABELS.service"
          size="sm"
        />
        <Input
          v-model="draftRequest.requested_time"
          :label="UI_LABELS.requestedTime"
          size="sm"
        />
      </div>
      <Textarea
        v-model="draftRequest.note"
        :label="UI_LABELS.note"
        :max-length="500"
        resize
      />
      <div class="flex justify-end">
        <NextButton
          :label="UI_LABELS.add"
          :disabled="!canAddRequest"
          slate
          solid
          @click.prevent="addRequest"
        />
      </div>
    </div>

    <div v-if="!setupMode" class="grid gap-2">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.requests }}
      </h4>
      <p v-if="!booking.requests.length" class="mb-0 text-sm text-n-slate-10">
        {{ UI_LABELS.empty }}
      </p>
      <article
        v-for="request in booking.requests"
        :key="request.id"
        class="grid gap-2 rounded-md bg-n-alpha-2 p-3 sm:grid-cols-[1fr_auto]"
      >
        <div class="text-sm text-n-slate-12">
          <div class="font-medium">
            <span>{{ request.customer_name }}</span>
            <span aria-hidden="true">{{ TEXT_SEPARATOR }}</span>
            <span>{{ request.requested_time }}</span>
          </div>
          <div class="text-n-slate-11">
            {{ request.customer_contact }}
            <span v-if="request.service">
              <span aria-hidden="true">{{ DOT_SEPARATOR }}</span>
              <span>{{ request.service }}</span>
            </span>
          </div>
          <p v-if="request.note" class="mb-0 mt-1 text-n-slate-11">
            {{ request.note }}
          </p>
        </div>
        <select
          class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
          :value="request.status"
          @change="updateRequestStatus(request.id, $event.target.value)"
        >
          <option
            v-for="status in STATUS_OPTIONS"
            :key="status.value"
            :value="status.value"
          >
            {{ status.label }}
          </option>
        </select>
      </article>
    </div>
  </section>
</template>
